import {
  useMemo, useRef, useState
} from "react";
import { Swiper as SwiperClass } from "swiper/types";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@apollo/client";
import { App } from "antd";
import { useTranslation } from "react-i18next";
import useAppointments from "../../hooks/useAppointments";
import useCurrentUser from "../../hooks/useCurrentUser";
import { TimeType } from "../../types/scheduler";
import { ADD_APPOINTMENT } from "../Book/book.qql";
import { appRoutes } from "../../constants/routes";
import { useAppSelector } from "../../store";
import { currentDoctor } from "../../store/slices/user";
import { DATE_FORMAT } from "../../constants/date";

const usePatientAppointments = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const { appointments } = useAppointments();
  const navigate = useNavigate();
  const { data: userData, refetchUser } = useCurrentUser();
  const navigationPrevRef = useRef(null);
  const navigationNextRef = useRef(null);
  const swiperRef = useRef<SwiperClass | null>(null);
  const arrowPrevRef = useRef<HTMLButtonElement>(null);
  const arrowNextRef = useRef<HTMLButtonElement | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentDate, setCurrentDate] = useState("");
  const doctor = useAppSelector(currentDoctor);

  const [addAppointment, { data: addAppointmentData, loading }] = useMutation(ADD_APPOINTMENT, {
    fetchPolicy: "no-cache",

    onCompleted(data: { addAppointment: string }) {
      if (data && data.addAppointment.split(",")[0] === "id") {
        refetchUser();
        navigate(`${appRoutes.success}?appointment_id=${data.addAppointment.split(",")[1]}`);
      } else {
        message.error(t("apollo.error.addAppointment"));
      }
    },
    onError() {
      message.error(t("apollo.error.addAppointment"));
    }
  });

  const isSubmitButtonLoading = loading || addAppointmentData;

  const handlePrev = () => {
    if (swiperRef.current) {
      swiperRef.current.slidePrev();
    }
  };

  const handleNext = () => {
    if (swiperRef.current) {
      swiperRef.current.slideNext();
    }
  };

  const onSubmit = () => {
    if (!doctor) {
      return null;
    }

    return addAppointment({
      variables: {
        professionistId: +doctor.id,
        patientId: +userData.id,
        startAt: dayjs(currentDate).format(`${DATE_FORMAT.FULL_DATE} ${DATE_FORMAT.FULL_TIME}`)
      }
    });
  };

  const onCancel = () => {
    setIsModalOpen(false);
  };

  const handleOnClick = (item: TimeType) => {
    const selectedDate = dayjs(`${item.current} ${item.from}`);

    if (userData.credits) {
      setCurrentDate(selectedDate.toString());
      setIsModalOpen(true);
    } else {
      navigate(appRoutes.appointmentBook, {
        state: {
          activeDate: selectedDate.format("YYYY-MM-DD HH:mm:ss"),
        }
      });
    }
  };

  const selectedDate = useMemo(() => {
    return dayjs(currentDate).format("DD MMMM YYYY - Ore HH:mm");
  }, [currentDate]);

  return {
    appointments,
    navigationPrevRef,
    navigationNextRef,
    swiperRef,
    handlePrev,
    handleNext,
    isModalOpen,
    selectedDate,
    handleOnClick,
    onCancel,
    onSubmit,
    isSubmitButtonLoading,
    arrowPrevRef,
    arrowNextRef
  };
};

export default usePatientAppointments;
